import { useRouter } from "next/router";
import { Size } from "../../models/enums";
import { useAudioPlayer } from "../../src/contexts/AudioPlayerContext";
import KitArt from "../arts/kit-art";
import TrackArt from "../arts/track-art";
import Link from "next/link";
import ArtistArt from "../arts/artist-art";
import {
  getEntityType,
  getViewObjectUrl,
  isArtist,
  isBeat,
  isSong,
  isSoundkit,
  slugify,
} from "../../lib/helpers";
import { getLoadEntity } from "@/lib/audiolab-manager";
import { ViewObject } from "@/models/models";

import { CREATORS_SLUG_ID } from "@/constants/routes";

interface GridTileProps {
  item: any;
  playableTrack?: boolean;
  hoverEffect?: boolean;
  size?: Size;
  onClick?: Function;
  className?: string;
  onPlay?: (item: ViewObject) => void;
}

const GridTile = ({
  item,
  playableTrack = false,
  hoverEffect = false,
  size = Size.lg,
  onClick,
  onPlay,
  className,
}: GridTileProps) => {
  // hooks
  const router = useRouter();
  const audioPlayer = useAudioPlayer();

  const navigateTo = (item: any) => {
    getLoadEntity({ id: item.id, type: getEntityType(item) });
    router.push(getViewObjectUrl(item));
  };

  const handleOnPlay = () => {
    if (item) {
      onPlay?.(item);
    }

    if (
      item &&
      ((item?.type === "sounds" && item?.sample) || item?.type !== "sounds")
    ) {
      audioPlayer.handlePlayTrack(item);
      onClick && onClick();
    }
  };

  return (
    <div
      className={`flex p-2 md:p-4 flex-col space-y-1 rounded-3xl ${
        hoverEffect && "hover:bg-backgroundLight"
      } ${
        audioPlayer.selectedAudio?.id === item.id && "bg-backgroundLight"
      } ${className}`}
    >
      {isSoundkit(item) && (
        <KitArt
          id={`grid-soundkitArt-play-${item.id}`}
          item={item}
          play={item.sample ? playableTrack : false}
          onPlay={handleOnPlay}
          onClick={() => navigateTo(item)}
          className="mb-3"
        />
      )}
      {(isBeat(item) || isSong(item)) && (
        <TrackArt
          id={`grid-trackArt-play-${item.id}`}
          item={item}
          size={size}
          play={playableTrack}
          onPlay={handleOnPlay}
          onClick={() => navigateTo(item)}
          className="mb-3"
        />
      )}
      {isArtist(item) && (
        <ArtistArt
          id={`grid-creatorArt-navigate-${item.id}`}
          artist={item}
          size={Size.lg}
          className="mb-3"
        />
      )}

      <div className="flex flex-col mt-5 space-y-1">
        <div className="flex items-center space-x-1 space-y-0">
          {(isSoundkit(item) || isBeat(item) || isSong(item)) && (
            <p className="text-purple font-bold leading-none">
              ${item.price?.toFixed(2)}
            </p>
          )}
          {(isBeat(item) || isSong(item)) && (
            <>
              <span className="h-[4px] w-[4px] rounded-full bg-subtitle"></span>{" "}
              <span className="text-subtitle font-normal leading-none">
                {item.tempo} BPM
              </span>
            </>
          )}
        </div>
        <Link
          className="flex w-full truncate"
          href={getViewObjectUrl(item)}
        >
          <h4 className="text-start text-base lg:text-2xl font-bold w-full hover:underline min-w-[117px] truncate hover:overflow-visible hover:animate-marquee-fast">
            {item.name}
          </h4>
        </Link>

        <button
          id={`grid-btn-creatorName-${item.id}`}
          onClick={() =>
            router.push({
              pathname: CREATORS_SLUG_ID,
              query: {
                slug: slugify(item.artist?.name),
                id: item.artist?.id
              }
            })
          }
          className="grid-btn-creatorName flex text-sm lg:text-base text-subtitle cursor-pointer hover:underline"
        >
          <span className="truncate">{item.artist?.name}</span>
        </button>
      </div>
    </div>
  );
};

export default GridTile;
